import React, { useRef, useEffect } from 'react';
import { CheckCircle2, Clock, X, SkipForward, GitBranch } from 'lucide-react';
import { Button, StatusBadge, ICON_PX } from '../ui';
import CompletionForm from './CompletionForm';

const STATUS_DISPLAY = {
  satisfied: { label: 'Done', variant: 'on-track', color: 'border-[var(--green)]', bg: 'bg-green-bg' },
  available: { label: 'Up Next', variant: 'info', color: 'border-[var(--aqua)]', bg: 'bg-[var(--aqua)]/5' },
  blocked: { label: 'Not Yet', variant: 'watch', color: 'border-slate-200', bg: 'bg-slate-50' },
  skipped: { label: 'Skipped', variant: 'watch', color: 'border-slate-200', bg: 'bg-slate-50' },
  pending: { label: 'Pending', variant: 'info', color: 'border-slate-200', bg: 'bg-white' },
};

/**
 * Floating popover that appears near a clicked node on the canvas in execute mode.
 * Shows node status and inline completion form for available tasks.
 */
function NodeExecutionPopover({
  node,
  nodeState,
  position,
  onCompleteTask,
  onSkipTask,
  onGateSelect,
  onClose,
}) {
  const popoverRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target)) {
        onClose?.();
      }
    };
    // Delay to avoid immediate close from the click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClick);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClick);
    };
  }, [onClose]);

  if (!node || !position) return null;

  const status = nodeState?.status || 'pending';
  const display = STATUS_DISPLAY[status] || STATUS_DISPLAY.pending;
  const isTask = node.type === 'task';
  const isGate = node.type === 'gate';
  const isAvailable = status === 'available';
  const isSatisfied = status === 'satisfied';

  // Position: try to show below and to the right of the node
  const style = {
    left: Math.min(position.x, window.innerWidth - 320),
    top: Math.min(position.y + 10, window.innerHeight - 400),
  };

  return (
    <div
      ref={popoverRef}
      className={`fixed z-50 w-[280px] rounded-xl border shadow-lg overflow-hidden ${display.color} ${display.bg}`}
      style={style}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-slate-100 bg-white/80 backdrop-blur-sm">
        <div className="flex items-center gap-2 min-w-0">
          <StatusBadge variant={display.variant}>{display.label}</StatusBadge>
          <span className="text-sm font-medium text-text-dark truncate">{node.data?.label || node.label}</span>
        </div>
        <button type="button" onClick={onClose} className="text-slate-400 hover:text-text-dark p-0.5 rounded shrink-0">
          <X size={ICON_PX.sm} />
        </button>
      </div>

      {/* Body */}
      <div className="px-3 py-3 max-h-[300px] overflow-y-auto">
        {/* ── Satisfied: show completion details ── */}
        {isSatisfied && nodeState && (
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5 text-sm text-green-nn font-medium">
              <CheckCircle2 size={ICON_PX.md} /> Completed
            </div>
            {nodeState.satisfiedBy && (
              <div className="text-xs text-slate-500">By: {nodeState.satisfiedBy}</div>
            )}
            {nodeState.satisfiedAt && (
              <div className="text-xs text-slate-500">On: {new Date(nodeState.satisfiedAt).toLocaleString()}</div>
            )}
          </div>
        )}

        {/* ── Available task: inline completion form ── */}
        {isAvailable && isTask && (
          <div className="space-y-3">
            <CompletionForm
              completionType={node.data?.completionType || 'checkbox'}
              compact
              onSubmit={(data) => {
                onCompleteTask?.(node, data);
                onClose?.();
              }}
            />
            <button
              type="button"
              onClick={() => {
                onSkipTask?.(node);
                onClose?.();
              }}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
            >
              <SkipForward size={12} />
              Skip this step
            </button>
          </div>
        )}

        {/* ── Available gate: path selection ── */}
        {isAvailable && isGate && (
          <div className="space-y-2.5">
            <div className="flex items-center gap-1.5 text-sm text-slate-600">
              <GitBranch size={ICON_PX.sm} className="text-[var(--aqua)]" />
              Which path?
            </div>
            <div className="flex gap-2">
              {(node.data?.options || ['Yes', 'No']).map((opt) => (
                <Button
                  key={opt}
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => {
                    onGateSelect?.(node.id || node.data?.id, opt);
                    onClose?.();
                  }}
                >
                  {opt}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* ── Blocked: waiting info ── */}
        {status === 'blocked' && (
          <div className="flex items-center gap-2 text-sm text-slate-500">
            <Clock size={ICON_PX.md} className="text-slate-400 shrink-0" />
            <span>Waiting for previous steps to complete.</span>
          </div>
        )}

        {/* ── Pending ── */}
        {status === 'pending' && (
          <div className="text-sm text-slate-400">
            This step hasn't started yet.
          </div>
        )}
      </div>
    </div>
  );
}

export default NodeExecutionPopover;
